import React, { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { DateTimePicker } from '@/components/ui/datetime-picker';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import {
  Users,
  CheckSquare,
  Square,
  Zap,
  MapPin,
  Clock,
  Star,
  ArrowRight,
  AlertTriangle,
  Calendar,
  User
} from 'lucide-react';

interface RecallItem {
  vin: string;
  campaign_number: string;
  recall_title: string;
  dealer_name: string;
  dealer_city: string;
  dealer_region: string;
  dealer_street?: string;
  dealer_address?: string;
  skill_level?: number;
  car_year?: string | number;
  make_name?: string;
  model_name?: string;
}

interface Technician {
  id: string;
  full_name: string;
  skill_level: number;
  phone_number: string;
  email: string;
  current_assignments?: number;
  estimated_drive_time?: number;
  specialization?: string;
}

interface Assignment {
  id: string;
  vin: string;
  campaign_number: string;
  technician_id: string;
  technician_name?: string;
  expected_visit_date?: string;
  notes?: string;
}

interface BatchAssignmentPanelProps {
  recalls: RecallItem[];
  technicians: Technician[];
  assignments: Assignment[]; // Existing assignments
  onBatchAssign: (
    recallIds: string[],
    technicianId: string,
    notes: string,
    expectedVisitDate?: Date
  ) => void;
  onToggleSelect: (recallId: string) => void;
  selectedRecalls: Set<string>;
  isCreating: boolean;
}

const BatchAssignmentPanel: React.FC<BatchAssignmentPanelProps> = ({
  recalls,
  technicians,
  assignments,
  onBatchAssign,
  onToggleSelect,
  selectedRecalls,
  isCreating
}) => {
  const [selectedTechnician, setSelectedTechnician] = useState<string>('');
  const [batchNotes, setBatchNotes] = useState<string>('');
  const [expectedVisitDate, setExpectedVisitDate] = useState<Date | undefined>(new Date()); // Default to today
  const [showSmartSuggestions, setShowSmartSuggestions] = useState<boolean>(true);
  const hasAutoSelected = useRef(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [reassignmentInfo, setReassignmentInfo] = useState<{
    existingAssignments: Assignment[];
    newTechnicianId: string;
    newDate: Date;
    notes: string;
  } | null>(null);

  // Group recalls by VIN
  const groupedByVin = React.useMemo(() => {
    const groups = new Map<string, RecallItem[]>();
    recalls.forEach(recall => {
      if (!groups.has(recall.vin)) {
        groups.set(recall.vin, []);
      }
      groups.get(recall.vin)!.push(recall);
    });
    return Array.from(groups.entries()).map(([vin, recalls]) => ({
      vin,
      recalls,
      car_year: recalls[0]?.car_year || 'Unknown',
      make_name: recalls[0]?.make_name || 'Unknown',
      model_name: recalls[0]?.model_name || 'Model'
    }));
  }, [recalls]);

  // Auto-select all VINs on mount
  useEffect(() => {
    // Only auto-select once when data is loaded
    if (groupedByVin.length > 0 && !hasAutoSelected.current) {
      // Select all VINs
      groupedByVin.forEach(group => {
        onToggleSelect(group.vin);
      });
      hasAutoSelected.current = true;
    }
  }, [groupedByVin, onToggleSelect]);

  // Smart suggestions based on proximity and skills
  const getSmartSuggestions = (): Technician[] => {
    if (!selectedRecalls.size) return [];

    const selectedRecallData = recalls.filter(r => selectedRecalls.has(r.vin));
    const avgSkillLevel =
      selectedRecallData.reduce((sum, r) => sum + (r.skill_level || 2), 0) /
      selectedRecallData.length;

    return technicians
      .map(tech => {
        let score = 0;

        // Skill level matching (40% weight)
        const skillMatch = Math.abs(tech.skill_level - avgSkillLevel) <= 1 ? 1 : 0.5;
        score += skillMatch * 40;

        // Workload balancing (30% weight)
        const workloadScore = Math.max(0, 1 - (tech.current_assignments || 0) / 10);
        score += workloadScore * 30;

        // Drive time efficiency (20% weight)
        const driveScore = tech.estimated_drive_time
          ? Math.max(0, 1 - tech.estimated_drive_time / 60)
          : 0.5;
        score += driveScore * 20;

        // Specialization bonus (10% weight)
        const specializationBonus = selectedRecallData.some(
          r =>
            tech.specialization &&
            r.recall_title.toLowerCase().includes(tech.specialization.toLowerCase())
        )
          ? 10
          : 0;
        score += specializationBonus;

        return { ...tech, suggestionScore: score };
      })
      .sort((a, b) => (b as any).suggestionScore - (a as any).suggestionScore)
      .slice(0, 3);
  };

  const smartSuggestions = getSmartSuggestions();
  const selectedCount = selectedRecalls.size;

  const handleSelectAllUnassigned = () => {
    unassignedVins.forEach(group => {
      if (!selectedRecalls.has(group.vin)) {
        onToggleSelect(group.vin);
      }
    });
  };

  const handleSelectAllAssigned = () => {
    assignedVins.forEach(group => {
      if (!selectedRecalls.has(group.vin)) {
        onToggleSelect(group.vin);
      }
    });
  };

  const handleDeselectAll = () => {
    selectedRecalls.forEach(recallId => {
      onToggleSelect(recallId);
    });
  };

  // Helper function to get assignment for a recall
  const getAssignmentForRecall = (vin: string, campaignNumber: string): Assignment | undefined => {
    return assignments.find(a => a.vin === vin && a.campaign_number === campaignNumber);
  };

  // Helper function to check if any recall for a VIN is assigned
  const isVinAssigned = (vin: string): boolean => {
    const vinRecalls = recalls.filter(r => r.vin === vin);
    return vinRecalls.some(r => getAssignmentForRecall(r.vin, r.campaign_number));
  };

  // Separate VINs by assignment status
  const unassignedVins = groupedByVin.filter(g => !isVinAssigned(g.vin));
  const assignedVins = groupedByVin.filter(g => isVinAssigned(g.vin));

  // Helper function to convert Date to ISO datetime string (for TIMESTAMPTZ storage)
  const dateToISOString = (date: Date): string => {
    return date.toISOString();
  };

  // Helper function to format datetime string for display
  // Handles both ISO datetime strings and date-only strings (YYYY-MM-DD) for backward compatibility
  const formatDateStringForDisplay = (
    dateString: string,
    format: 'short' | 'long' = 'long'
  ): string => {
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return dateString; // Invalid date, return as-is
      }

      // Always include time in display (since we now support time scheduling)
      if (format === 'short') {
        // Short format: Month Day, Time (e.g., "Dec 24, 2:30 PM")
        return date.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
          hour12: true
        });
      }

      // Long format: Weekday, Month Day, Year, Time (e.g., "Wed, Dec 24, 2024, 2:30 PM")
      return date.toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    } catch {
      return dateString;
    }
  };

  // Helper function to check if reassignment is needed
  const checkForReassignments = (
    selectedVINs: string[],
    technicianId: string,
    date: Date | undefined
  ): Assignment[] => {
    if (!date) return [];

    const recallsToAssign = recalls.filter(recall => selectedVINs.includes(recall.vin));
    const existingAssignments: Assignment[] = [];

    // Convert date to ISO datetime string
    const dateString =
      date instanceof Date ? dateToISOString(date) : date ? new Date(date).toISOString() : '';

    recallsToAssign.forEach(recall => {
      const assignment = getAssignmentForRecall(recall.vin, recall.campaign_number);
      if (assignment) {
        // Check if it's being reassigned to different technician or date
        const isDifferentTech = assignment.technician_id !== technicianId;
        const isDifferentDate =
          assignment.expected_visit_date && assignment.expected_visit_date !== dateString;

        if (isDifferentTech || isDifferentDate) {
          existingAssignments.push(assignment);
        }
      }
    });

    return existingAssignments;
  };

  const handleBatchAssign = () => {
    if (selectedCount === 0 || !selectedTechnician || !expectedVisitDate) {
      return;
    }

    // Check for existing assignments that would be reassigned
    const existingAssignments = checkForReassignments(
      Array.from(selectedRecalls),
      selectedTechnician,
      expectedVisitDate
    );

    if (existingAssignments.length > 0) {
      // Show confirmation dialog
      setReassignmentInfo({
        existingAssignments,
        newTechnicianId: selectedTechnician,
        newDate: expectedVisitDate,
        notes: batchNotes
      });
      setShowConfirmDialog(true);
      return;
    }

    // No conflicts, proceed with assignment
    onBatchAssign(Array.from(selectedRecalls), selectedTechnician, batchNotes, expectedVisitDate);
    setSelectedTechnician('');
    setBatchNotes('');
    setExpectedVisitDate(new Date());
  };

  const handleConfirmReassignment = () => {
    if (!reassignmentInfo) return;

    onBatchAssign(
      Array.from(selectedRecalls),
      reassignmentInfo.newTechnicianId,
      reassignmentInfo.notes,
      reassignmentInfo.newDate
    );

    setShowConfirmDialog(false);
    setReassignmentInfo(null);
    setSelectedTechnician('');
    setBatchNotes('');
    setExpectedVisitDate(new Date());
  };

  const handleCancelReassignment = () => {
    setShowConfirmDialog(false);
    setReassignmentInfo(null);
  };

  const getSkillLevelLabel = (level: number) => {
    switch (level) {
      case 1:
        return 'Low';
      case 2:
        return 'Medium';
      case 3:
        return 'High';
      default:
        return 'Unset';
    }
  };

  const getSkillLevelColor = (level: number) => {
    switch (level) {
      case 1:
        return 'border-red-500 text-red-700 bg-red-50';
      case 2:
        return 'border-yellow-500 text-yellow-700 bg-yellow-50';
      case 3:
        return 'border-green-500 text-green-700 bg-green-50';
      default:
        return 'border-gray-500 text-gray-700 bg-gray-50';
    }
  };

  return (
    <div className='space-y-4'>
      {/* Recalls Selection */}
      <Card>
        <CardHeader>
          <div className='flex items-center justify-between'>
            <CardTitle className='flex items-center gap-2'>
              <CheckSquare className='w-5 h-5' />
              Select Recalls for Batch Assignment
            </CardTitle>
            <div className='flex items-center gap-2'>
              <Button
                variant='outline'
                size='sm'
                onClick={handleSelectAllUnassigned}
                disabled={
                  unassignedVins.length === 0 ||
                  unassignedVins.every(g => selectedRecalls.has(g.vin))
                }
              >
                <CheckSquare className='w-4 h-4 mr-1' />
                Select Unassigned ({unassignedVins.length})
              </Button>
              <Button
                variant='outline'
                size='sm'
                onClick={handleSelectAllAssigned}
                disabled={
                  assignedVins.length === 0 || assignedVins.every(g => selectedRecalls.has(g.vin))
                }
              >
                <CheckSquare className='w-4 h-4 mr-1' />
                Select Assigned ({assignedVins.length})
              </Button>
              <Button
                variant='outline'
                size='sm'
                onClick={handleDeselectAll}
                disabled={selectedCount === 0}
              >
                <Square className='w-4 h-4 mr-1' />
                Deselect All
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className='mb-4'>
            <Badge variant='secondary' className='text-sm bg-blue-100 text-blue-700'>
              {selectedCount} of {groupedByVin.length} vehicles selected • {recalls.length} total
              recalls
            </Badge>
          </div>

          {/* Vehicle List with Recalls */}
          <div className='max-h-[600px] overflow-y-auto space-y-3 border rounded-lg p-3 bg-gray-50'>
            {groupedByVin.map(group => {
              const isSelected = selectedRecalls.has(group.vin);
              return (
                <div
                  key={group.vin}
                  className={`rounded-lg border transition-colors ${
                    isSelected ? 'bg-blue-50 border-blue-300' : 'bg-white border-gray-200'
                  }`}
                >
                  {/* Vehicle Header - Clickable */}
                  <div
                    className='flex items-start gap-3 p-4 cursor-pointer hover:bg-opacity-80'
                    onClick={e => {
                      // Only toggle if clicking outside the checkbox
                      if (!(e.target as HTMLElement).closest('[role="checkbox"]')) {
                        onToggleSelect(group.vin);
                      }
                    }}
                  >
                    <div onClick={e => e.stopPropagation()}>
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={checked => {
                          // Only toggle if the checkbox state doesn't match
                          if ((checked && !isSelected) || (!checked && isSelected)) {
                            onToggleSelect(group.vin);
                          }
                        }}
                        className='mt-1'
                      />
                    </div>
                    <div className='flex-1 min-w-0'>
                      {/* Primary: Car Details */}
                      <div className='font-semibold text-base text-gray-900 mb-2'>
                        {group.car_year} {group.make_name} {group.model_name}
                      </div>

                      {/* Badge showing recall count */}
                      <div className='flex items-center gap-2 mb-2'>
                        <Badge
                          variant='outline'
                          className='text-xs bg-orange-50 text-orange-700 border-orange-300'
                        >
                          {group.recalls.length} recall{group.recalls.length !== 1 ? 's' : ''}
                        </Badge>
                      </div>

                      {/* Recalls List */}
                      <div className='space-y-2 mt-3'>
                        {group.recalls.map(recall => {
                          const existingAssignment = getAssignmentForRecall(
                            recall.vin,
                            recall.campaign_number
                          );

                          return (
                            <div
                              key={`${recall.vin}-${recall.campaign_number}`}
                              className={`pl-3 border-l-2 ${
                                existingAssignment ? 'border-green-400' : 'border-gray-300'
                              }`}
                            >
                              <div className='flex items-start justify-between gap-3'>
                                <div className='flex-1 min-w-0'>
                                  <div className='flex items-center gap-2 mb-1'>
                                    <Badge variant='outline' className='text-xs shrink-0'>
                                      {recall.campaign_number}
                                    </Badge>
                                    <span className='text-sm font-medium text-gray-800'>
                                      {recall.recall_title}
                                    </span>
                                  </div>
                                  <div className='text-xs text-gray-600 mt-1 space-y-0.5'>
                                    <div>
                                      VIN: <span className='font-mono'>{recall.vin}</span>
                                    </div>
                                    <div>
                                      Dealer: {recall.dealer_name}
                                      {(recall.dealer_street || recall.dealer_address) && (
                                        <span className='ml-1'>
                                          • {recall.dealer_street || recall.dealer_address},{' '}
                                          {recall.dealer_city}, {recall.dealer_region}
                                          {recall.dealer_postal_code
                                            ? ` ${recall.dealer_postal_code}`
                                            : ''}
                                        </span>
                                      )}
                                      {!(recall.dealer_street || recall.dealer_address) && (
                                        <span className='ml-1'>
                                          • {recall.dealer_city}, {recall.dealer_region}
                                          {recall.dealer_postal_code
                                            ? ` ${recall.dealer_postal_code}`
                                            : ''}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </div>

                                <div className='flex flex-col items-end gap-1 shrink-0'>
                                  {/* Show existing assignment on the right */}
                                  {existingAssignment && (
                                    <div className='p-2 bg-green-50 border border-green-200 rounded text-right'>
                                      <div className='flex items-start gap-1.5'>
                                        <User className='w-3.5 h-3.5 text-green-600 mt-0.5 shrink-0' />
                                        <div className='text-xs text-green-800'>
                                          <div className='font-medium whitespace-nowrap'>
                                            {existingAssignment.technician_name || 'Assigned'}
                                          </div>
                                          {existingAssignment.expected_visit_date && (
                                            <div className='flex items-center gap-1 mt-0.5 justify-end'>
                                              <Calendar className='w-3 h-3' />
                                              <span className='whitespace-nowrap'>
                                                {formatDateStringForDisplay(
                                                  existingAssignment.expected_visit_date,
                                                  'short'
                                                )}
                                              </span>
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  )}

                                  {recall.skill_level && (
                                    <Badge
                                      className={`text-xs ${getSkillLevelColor(recall.skill_level)}`}
                                    >
                                      {getSkillLevelLabel(recall.skill_level)}
                                    </Badge>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Batch Assignment Form */}
      {selectedCount > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className='text-lg'>Assign Selected Recalls to Technician</CardTitle>
          </CardHeader>
          <CardContent>
            <div className='space-y-4'>
              {/* Smart Suggestions */}
              {showSmartSuggestions && smartSuggestions.length > 0 && (
                <div className='bg-blue-50 border border-blue-200 rounded-lg p-4'>
                  <div className='flex items-center gap-2 mb-3'>
                    <Zap className='w-5 h-5 text-blue-600' />
                    <h4 className='font-medium text-blue-900'>Smart Suggestions</h4>
                    <Button
                      variant='ghost'
                      size='sm'
                      onClick={() => setShowSmartSuggestions(false)}
                      className='ml-auto text-blue-600 hover:text-blue-700'
                    >
                      Hide
                    </Button>
                  </div>

                  <div className='space-y-2'>
                    {smartSuggestions.map(tech => (
                      <div
                        key={tech.id}
                        className='flex items-center justify-between p-3 bg-white rounded-lg border border-blue-100 cursor-pointer hover:border-blue-300 transition-colors'
                        onClick={() => setSelectedTechnician(tech.id)}
                      >
                        <div className='flex items-center gap-3'>
                          <div className='flex items-center gap-2'>
                            <Star className='w-4 h-4 text-yellow-500' />
                            <span className='text-sm font-medium text-gray-900'>
                              {tech.full_name}
                            </span>
                          </div>
                          <Badge className={getSkillLevelColor(tech.skill_level)}>
                            {getSkillLevelLabel(tech.skill_level)}
                          </Badge>
                          {tech.specialization && (
                            <Badge variant='outline' className='text-xs'>
                              {tech.specialization}
                            </Badge>
                          )}
                        </div>

                        <div className='flex items-center gap-4 text-sm text-gray-600'>
                          {tech.current_assignments !== undefined && (
                            <div className='flex items-center gap-1'>
                              <Users className='w-4 h-4' />
                              {tech.current_assignments} assigned
                            </div>
                          )}
                          {tech.estimated_drive_time && (
                            <div className='flex items-center gap-1'>
                              <Clock className='w-4 h-4' />~{tech.estimated_drive_time}min drive
                            </div>
                          )}
                          <div className='flex items-center gap-1'>
                            <MapPin className='w-4 h-4' />
                            {Math.round((tech as any).suggestionScore)}% match
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Batch Assignment Form */}
              <div className='space-y-3'>
                <div className='flex items-center gap-3'>
                  <div className='w-72'>
                    <Select value={selectedTechnician} onValueChange={setSelectedTechnician}>
                      <SelectTrigger>
                        <SelectValue placeholder='Select technician...' />
                      </SelectTrigger>
                      <SelectContent>
                        {technicians.map(technician => (
                          <SelectItem key={technician.id} value={technician.id}>
                            <div className='flex items-center gap-3 py-1'>
                              <div className='w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center'>
                                <Users className='w-4 h-4 text-blue-600' />
                              </div>
                              <div className='flex-1'>
                                <div className='font-medium text-sm text-gray-900'>
                                  {technician.full_name}
                                </div>
                                <div className='flex items-center gap-2'>
                                  <Badge className={getSkillLevelColor(technician.skill_level)}>
                                    {getSkillLevelLabel(technician.skill_level)}
                                  </Badge>
                                  {technician.current_assignments !== undefined && (
                                    <span className='text-xs text-gray-500'>
                                      {technician.current_assignments} current assignments
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className='flex flex-col'>
                    <DateTimePicker
                      value={expectedVisitDate}
                      onChange={setExpectedVisitDate}
                      datePlaceholder='Select date'
                      timePlaceholder='HH:MM'
                      className='w-full min-w-[280px]'
                      timeStep={30}
                    />
                    <p className='text-xs text-muted-foreground mt-1'>
                      Scheduled service date and time for all selected recalls
                    </p>
                  </div>

                  <Input
                    placeholder='Batch notes (optional)'
                    value={batchNotes}
                    onChange={e => setBatchNotes(e.target.value)}
                    className='flex-1 min-w-[200px]'
                  />
                </div>

                <Button
                  onClick={handleBatchAssign}
                  disabled={!selectedTechnician || !expectedVisitDate || isCreating}
                  className='flex items-center gap-2 w-full'
                  size='lg'
                >
                  <ArrowRight className='w-4 h-4' />
                  Assign {selectedCount} Recall{selectedCount !== 1 ? 's' : ''} to{' '}
                  {selectedTechnician
                    ? technicians.find(t => t.id === selectedTechnician)?.full_name
                    : 'Technician'}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Reassignment Confirmation Dialog */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent className='sm:max-w-[600px]'>
          <DialogHeader>
            <DialogTitle className='flex items-center gap-2'>
              <AlertTriangle className='w-5 h-5 text-amber-500' />
              Confirm Reassignment
            </DialogTitle>
            <DialogDescription>
              Some recalls are already assigned to technicians. Do you want to reassign them?
            </DialogDescription>
          </DialogHeader>

          <div className='py-4 max-h-[400px] overflow-y-auto'>
            <div className='space-y-3'>
              {reassignmentInfo?.existingAssignments.map((assignment, idx) => {
                const recall = recalls.find(
                  r => r.vin === assignment.vin && r.campaign_number === assignment.campaign_number
                );
                const newTechnician = technicians.find(
                  t => t.id === reassignmentInfo.newTechnicianId
                );

                return (
                  <div key={idx} className='p-3 border border-amber-200 bg-amber-50 rounded-lg'>
                    <div className='font-medium text-sm text-gray-900 mb-2'>
                      {recall?.recall_title || 'Recall'}
                    </div>
                    <div className='text-xs text-gray-600 space-y-1'>
                      <div>
                        Campaign: {assignment.campaign_number} • VIN: {assignment.vin}
                      </div>

                      <div className='flex items-center gap-2 mt-2'>
                        <div className='flex-1'>
                          <div className='font-medium text-xs text-gray-700'>Current:</div>
                          <div className='flex items-center gap-1 text-red-700'>
                            <User className='w-3 h-3' />
                            {assignment.technician_name}
                          </div>
                          {assignment.expected_visit_date && (
                            <div className='flex items-center gap-1 text-red-700'>
                              <Calendar className='w-3 h-3' />
                              {formatDateStringForDisplay(assignment.expected_visit_date, 'short')}
                            </div>
                          )}
                        </div>

                        <ArrowRight className='w-4 h-4 text-gray-400' />

                        <div className='flex-1'>
                          <div className='font-medium text-xs text-gray-700'>New:</div>
                          <div className='flex items-center gap-1 text-green-700'>
                            <User className='w-3 h-3' />
                            {newTechnician?.full_name}
                          </div>
                          {reassignmentInfo.newDate && (
                            <div className='flex items-center gap-1 text-green-700'>
                              <Calendar className='w-3 h-3' />
                              {reassignmentInfo.newDate.toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric'
                              })}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className='mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg'>
              <div className='text-sm text-blue-900'>
                <strong>Note:</strong> This will update{' '}
                {reassignmentInfo?.existingAssignments.length} existing assignment
                {reassignmentInfo?.existingAssignments.length !== 1 ? 's' : ''}.
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant='outline' onClick={handleCancelReassignment}>
              Cancel
            </Button>
            <Button onClick={handleConfirmReassignment} className='bg-amber-600 hover:bg-amber-700'>
              Confirm Reassignment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BatchAssignmentPanel;
